import { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Sheet, SheetContent, SheetTrigger, SheetTitle } from "./ui/sheet";
import { haptic } from "../lib/haptics";
import {
  Plus, Briefcase, Lightning, Folder as FolderIcon, FileText, Receipt, User, Buildings, X,
} from "@phosphor-icons/react";

/** Phase 235 — Mobile Floating Action Button.
 * Sits above the bottom-tab nav, visible only on mobile (md:hidden).
 * Tapping opens a native-feel bottom sheet of quick-create options.
 * Hidden on auth pages and on the supplier portal (vendors don't create jobs/projects). */
export default function MobileFAB({ user }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);

  // Hide on auth pages + on the vendor's portal (they create only quotes from their own page)
  const path = location.pathname;
  const hideOn = ["/login", "/signup", "/forgot-password", "/accept-invite", "/welcome"];
  if (hideOn.some(p => path.startsWith(p))) return null;
  if (!user) return null;
  // Suppliers get a smaller FAB straight to "create quote" only
  const isVendor = user?.role === "vendor";
  const isClient = user?.role === "client";

  const items = isClient ? [
    { label: "Job Request", icon: Briefcase, href: "/portal/requests/new", testid: "fab-client-request" },
    { label: "Emergency", icon: Lightning, href: "/portal/emergencies/new", testid: "fab-client-emergency", danger: true },
  ] : isVendor ? [
    { label: "New Quote", icon: FileText, href: "/supplier-portal?tab=quotes&new=1", testid: "fab-vendor-quote" },
  ] : [
    { label: "Job", icon: Briefcase, href: "/jobs?new=1", testid: "fab-job" },
    { label: "Emergency Job", icon: Lightning, href: "/emergency-jobs?new=1", testid: "fab-emergency", danger: true },
    { label: "Project", icon: FolderIcon, href: "/projects?new=1", testid: "fab-project" },
    { sep: true },
    { label: "Quote", icon: FileText, href: "/quotes?new=1", testid: "fab-quote" },
    { label: "Invoice", icon: Receipt, href: "/invoices?new=1", testid: "fab-invoice" },
    { sep: true },
    { label: "Client", icon: User, href: "/clients?new=1", testid: "fab-client" },
    { label: "Property", icon: Buildings, href: "/properties?new=1", testid: "fab-property" },
  ];

  const go = (href) => {
    haptic("medium");
    setOpen(false);
    navigate(href);
  };

  return (
    <Sheet open={open} onOpenChange={(v) => { if (v) haptic("light"); setOpen(v); }}>
      <SheetTrigger asChild>
        <button
          aria-label="Quick create"
          data-testid="mobile-fab"
          className="md:hidden fixed right-4 bottom-[calc(7.5rem+env(safe-area-inset-bottom))] z-40
                     h-14 w-14 rounded-full bg-primary text-primary-foreground shadow-xl
                     flex items-center justify-center active:scale-90 transition-transform
                     hover:shadow-2xl"
          style={{ boxShadow: "0 8px 24px rgba(0, 0, 0, 0.18), 0 4px 8px rgba(0, 0, 0, 0.12)" }}
        >
          <Plus size={24} weight="bold" />
        </button>
      </SheetTrigger>
      <SheetContent
        side="bottom"
        className="rounded-t-2xl pb-[calc(1rem+env(safe-area-inset-bottom))] max-h-[80vh]"
        data-testid="mobile-fab-sheet"
      >
        <div className="flex items-center justify-between mb-3">
          <SheetTitle className="text-base font-[Chivo]">Quick Create</SheetTitle>
          <button onClick={() => setOpen(false)} className="p-1.5 rounded-md hover:bg-muted" aria-label="Close">
            <X size={18} />
          </button>
        </div>
        <div className="grid grid-cols-3 gap-2">
          {items.map((it, i) =>
            it.sep ? (
              <div key={`sep-${i}`} className="col-span-3 h-px bg-border my-1" />
            ) : (
              <button
                key={it.testid}
                onClick={() => go(it.href)}
                className={`flex flex-col items-center justify-center gap-1.5 p-3 rounded-xl border border-border
                            active:scale-95 transition-transform hover:bg-muted
                            ${it.danger ? "text-red-600" : ""}`}
                data-testid={it.testid}
              >
                <it.icon size={22} weight="duotone" />
                <span className="text-[11px] font-medium leading-tight text-center">{it.label}</span>
              </button>
            )
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}
